import { type PropsWithChildren, useCallback } from 'react'
import React, { useMemo } from 'react'
import { intl } from 'lang/intl'

import { switchToPanel } from '../../../firebase/hooks/useFloor'
import { setChyron } from '../../../firebase/hooks/useOverlays'
import { setPanelToAuto, usePanel } from '../../../firebase/hooks/usePanel'
import { useLastActiveMediaTrayCard } from '../../../firebase/hooks/useTray'
import type { Mix } from '../../../firebase/model/mix'
import { useAutoLayout } from '../../../Stage/layouts'
import usePlaybackIntent from '../../../Webinar/hooks/usePlaybackIntent'
import { useSortedHumanParticipants } from '../../../webrtc/webRTCRoom'
import { MixPreview } from '../CustomMixes/MixPreview/MixPreview'

import './PanelMonitorContent.scss'

type PanelMonitorContentProps = PropsWithChildren<{
  isSelected?: boolean
}>

const PanelMonitorContent = ({ isSelected }: PanelMonitorContentProps) => {
  const { screen, participantIds, mode } = usePanel()
  const lastActiveMediaTrayCard = useLastActiveMediaTrayCard()
  const { setPlaybackPauseIntent, setPlaybackResumeIntent } =
    usePlaybackIntent()
  const participants = useSortedHumanParticipants()

  const visibleParticipants = useMemo(() => {
    if (mode === 'auto') {
      return participants.map((p) => p.id)
    }

    const participantIdsInRoom = participants.map((p) => p.id)
    return participantIds.filter((i) => participantIdsInRoom.includes(i))
  }, [mode, participantIds, participants])

  const participantCount = visibleParticipants.length

  const autoLayout = useAutoLayout(participantCount, !!screen)

  const mix: Mix = useMemo(() => {
    const m: Mix = {
      name: 'panel',
      id: '0',
      feeds: [],
    }

    if (screen && 'media' in autoLayout) {
      m.feeds.push({
        position: autoLayout.media,
        media:
          screen === 'tray'
            ? { type: 'tray', id: 0 }
            : { type: 'screen', id: screen },
      })
    }

    for (let i = 0; i < participantCount; i++) {
      m.feeds.push({
        position: autoLayout[i],
        media: { type: 'camera', id: visibleParticipants[i] },
      })
    }

    return m
  }, [autoLayout, participantCount, screen, visibleParticipants])

  const handlePanelClick = useCallback(() => {
    switchToPanel()

    if (!screen && participantIds.length === 0) {
      void setPanelToAuto()
    }

    void setChyron(null)
    if (screen !== 'tray') {
      void setPlaybackPauseIntent()
    } else if (lastActiveMediaTrayCard?.type === 'video') {
      void setPlaybackResumeIntent()
    }
  }, [
    lastActiveMediaTrayCard?.type,
    participantIds,
    screen,
    setPlaybackPauseIntent,
    setPlaybackResumeIntent,
  ])

  return (
    <button
      type="button"
      className="panel-monitor-content"
      onClick={handlePanelClick}
      aria-label={
        isSelected
          ? intl.formatMessage({
              defaultMessage: 'The selected speakers panel is shown',
            })
          : intl.formatMessage({
              defaultMessage:
                'Press enter or space to show the selected speakers panel',
            })
      }
    >
      <MixPreview mix={mix} />
    </button>
  )
}

export { PanelMonitorContent }
